import React, { useEffect } from 'react';
import { TouchableOpacity, ViewStyle } from 'react-native';
import Animated, {
    interpolateColor,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withSequence,
    withTiming,
} from 'react-native-reanimated';

import LockIcon from '@/assets/icons/lock-md';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { AppColors } from '@/components/lib/theme';
import { useIsProducerDown } from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useIsOptionSelected, useSelectionActions } from '@/feature/betslip-sbk/hooks/use-selection-actions';
import { toSelectionParam } from '@/feature/betslip-sbk/utils/to-selection-param';
import { useFormatOdds } from '@/hooks/use-format-odds';
import { useOddsDiffAnimation } from '@/hooks/useOddsDiffAnimation';
import { designSystem } from '@/styles/styles';
import { isOptionSuspended } from '@/utils/option-status';

import { useIsMarketPublished, useOptionOdds, useOptionStatus } from '../hooks/use-market-cache';
import { Event, Market, Option } from '../types';
import { OptionName } from './OptionName';

const AnimatedText = Animated.createAnimatedComponent(Text);

const DURATION_MS = 2000;

export const OPTION_BUTTON_HEIGHT = 62;

/**
 * Get border radius style based on grid position
 */
export const getBorderRadiusStyle = (row: number, col: number, totalRows: number, columns: number) => {
    const isFirstRow = row === 0;
    const isLastRow = row === totalRows - 1;
    const isFirstCol = col === 0;
    const isLastCol = col === columns - 1;

    return {
        borderTopLeftRadius: isFirstRow && isFirstCol ? 16 : 4,
        borderTopRightRadius: isFirstRow && isLastCol ? 16 : 4,
        borderBottomLeftRadius: isLastRow && isFirstCol ? 16 : 4,
        borderBottomRightRadius: isLastRow && isLastCol ? 16 : 4,
    };
};

/**
 * Get AnimatedStyles for option button
 */
export const useAnimatedOptionEffect = (
    odds: number,
    prevOdds: number,
    isAlreadySelected: boolean,
    diff: number,
    unselectedSourceColor?: string
) => {
    const progress = useSharedValue(0);

    const animatedColorStyle = useAnimatedStyle(() => {
        const srcColor = isAlreadySelected
            ? designSystem.colors.neutral900
            : unselectedSourceColor ?? ds.colorValue.text.brand;

        const destColor = diff > 0 ? ds.colorValue.text.success : diff < 0 ? ds.colorValue.text.danger : srcColor;

        return {
            color: interpolateColor(progress.value, [0, 1], [srcColor, destColor]),
        };
    }, [isAlreadySelected, diff]);

    useEffect(() => {
        if (prevOdds !== odds) {
            progress.value = withSequence(
                withTiming(0, { duration: 0 }),
                withTiming(1, { duration: DURATION_MS / 6 }),
                withDelay((DURATION_MS / 3) * 2, withTiming(0, { duration: DURATION_MS / 6 }))
            );
        }
    }, [prevOdds, odds, progress]);

    return { animatedColorStyle };
};

type OptionButtonProps = {
    option: Option;
    market: Market;
    event: Event;
    optionColor?: AppColors;
    onSelectionAdded?: () => void;
    testID?: string;
    buttonStyle?: ViewStyle[] | ViewStyle;
    isShortName?: boolean;
    hideOptionName?: boolean;
};

export const OptionButton = ({
    option,
    market,
    event,
    optionColor = ds.colorToken.background.neutral.subtler,
    onSelectionAdded,
    testID,
    buttonStyle,
    isShortName = false,
    hideOptionName = false,
}: OptionButtonProps) => {
    const { addSelection, removeSelection } = useSelectionActions();
    const odds = useOptionOdds(option.id) || option.odds;

    const { formatOdds } = useFormatOdds();
    const formattedOdds = formatOdds(odds);

    const optionStatus = useOptionStatus(option.id) || option.status;
    const isMarketPublished = useIsMarketPublished(market);
    const producerDown = useIsProducerDown(market.supplier);
    const isSuspended = isOptionSuspended(optionStatus, producerDown, isMarketPublished);
    const isAlreadySelected = useIsOptionSelected(option.id) && !isSuspended;

    const { animatedColorStyle } = useOddsDiffAnimation({ odds, isAlreadySelected });

    const toggleSelection = () => {
        if (isAlreadySelected) {
            removeSelection(option.id);
        } else {
            addSelection(
                toSelectionParam(
                    { ...option, odds, status: optionStatus },
                    { ...market, published: isMarketPublished },
                    event
                )
            );
            onSelectionAdded?.();
        }
    };

    return (
        <Box flex={1}>
            <TouchableOpacity
                onPress={toggleSelection}
                disabled={isSuspended}
                testID={`${option.description}-${testID}`}
            >
                <Box
                    backgroundColor={isAlreadySelected ? ds.colorToken.background.neutral.inverse : optionColor}
                    alignItems="center"
                    justifyContent="center"
                    paddingVertical="s8"
                    paddingHorizontal="s16"
                    borderRadius="r8"
                    style={buttonStyle}
                    height={OPTION_BUTTON_HEIGHT}
                >
                    {isSuspended ? (
                        <Box flex={1} justifyContent="center" alignItems="center">
                            <LockIcon />
                        </Box>
                    ) : (
                        <>
                            {!hideOptionName ? (
                                <OptionName
                                    optionType={option.option_type}
                                    marketType={market?.market_type}
                                    homeTeam={event.home_team}
                                    awayTeam={event.away_team}
                                    fallback={option.description}
                                    sportName={event.sport.name}
                                    variant="labelLarge2"
                                    numberOfLines={1}
                                    color={isAlreadySelected ? 'neutral900' : ds.colorToken.text.secondary}
                                    testID={`optionName-${testID}`}
                                    isShortName={isShortName}
                                />
                            ) : null}
                            <AnimatedText
                                allowFontScaling={false}
                                style={[animatedColorStyle]}
                                fontWeight="600"
                                fontSize={14}
                                lineHeight={20}
                                letterSpacing={-0.15}
                            >
                                {formattedOdds}
                            </AnimatedText>
                        </>
                    )}
                </Box>
            </TouchableOpacity>
        </Box>
    );
};
